// @flow
import { Map, List } from 'immutable';
import { actionPredicate, parseActionTypeAndState } from './index';
import type { Action } from '../../actions/types';
import { ALL_FETCH_TOPIC_TYPES } from '../../constants';

const hasMoreData = (action) => (action.payload ? action.payload.hasMore : false);
/**
 * Merge the result IDs of our Normalized response in the reducer state.
 *
 * @param action action blob
 * @param state Immutable reducer state
 * @param entityKey entity for which IDs are to be stored in our state
 * @param stateKey key under which to store these IDs
 * @returns {*} updated reducer state with entity IDs
 */
export const mergePayloadIdsIntoState = (
	state: Map<string, any>,
	action: Action,
	entityKey: string,
	stateKey: string,
) => {
	// normalizr data
	const { payload } = action;
	const payloadResults = payload ? payload.results || payload : null;
	if (!payloadResults) {
		// Return the state we already have.
		return state;
	} else if (!payloadResults.result) {
		// Naively merge, it handles actions that bypassed the promise-middleware layer
		return state.merge(state, payloadResults);
	} else if (action.succeeded) {
		// Merge IDs under 'stateKey' key
		return state.withMutations((state) => {
			const oldIds = state.get('result') || List();
			let newIds = List(payloadResults.result[entityKey] || [payloadResults.result]);
			newIds = newIds.filter((newId) => !oldIds.includes(newId));
			let finalIds = newIds.concat(oldIds);
			if (newIds.size > 0 && ALL_FETCH_TOPIC_TYPES.has(action.actionType)) {
				// If we have a topic from Confluence Cloud and Local DB, we need to remove the local one from the list.
				const oldIdToTitle = state.get('idToTitle') || Map();
				const newIdToTitle = Map(
					Object.values(payloadResults.entities[entityKey]).reduce((obj, entity) => {
						obj[entity.id] = entity.title;
						return obj;
					}, {}),
				);
				const idToTitle = oldIdToTitle.merge(newIdToTitle);

				const oldTitleToId = state.get('titleToId') || Map();
				const newTitleToId = Map(
					Object.values(payloadResults.entities[entityKey]).reduce(
						(obj, { id, title, confluenceCloud }) => {
							obj[title] = { id, confluenceCloud };
							return obj;
						},
						{},
					),
				);
				const titleToId = oldTitleToId.mergeWith(
					(originalMapValue, mergedMapValue) =>
						originalMapValue.confluenceCloud ? originalMapValue : mergedMapValue,
					newTitleToId,
				);

				finalIds = finalIds.filter((id) => {
					const title = idToTitle.get(id);
					const { id: savedId } = titleToId.get(title);
					return savedId === id;
				});

				state.set('idToTitle', idToTitle);
				state.set('titleToId', titleToId);
			}

			state.set(stateKey, finalIds);
			state.set('hasMore', hasMoreData(action));

			if (payload._links) {
				const stateLinks = state.get('_links', Map({}));
				const newLinks = stateLinks.merge(Map(payload._links));

				state.set('_links', newLinks);
			}

			if (payload.next) {
				state.set('next', payload.next);
			}
		});
	}
	return state;
};

/**
 * Replace the result IDs of our Normalized response in the reducer state.
 *
 * @param action action data
 * @param state Immutable reducer state
 * @param entityKey entity for which IDs are to be stored in our state
 * @param stateKey key under which to store these IDs
 * @returns {*} updated reducer state with entity IDs
 */
export const replacePayloadIdsIntoState = (
	state: Map<string, any>,
	action: Action,
	entityKey: string,
	stateKey: string,
) => {
	// normalizr data
	const { payload } = action;
	const payloadResults = payload ? payload.results || payload : null;

	if (!payloadResults || !payloadResults.result) {
		// Return the state we already have.
		return state;
	}
	// replace IDs under 'stateKey' key.
	return state.withMutations((state) => {
		state.set(stateKey, List(payloadResults.result[entityKey] || [payloadResults.result]));
		state.set('hasMore', hasMoreData(action));
	});
};

/**
 * Remove single entity from the state.
 *
 * @param state
 * @param action
 * @returns {*}
 */
export const removeFromState = (state: Map<string, any>, action: Action) => {
	if (action.succeeded) {
		const result = state.get('result');
		if (result) {
			const entityIndex = result.indexOf(action.params.entityId);
			const results = result.remove(entityIndex);
			return state.withMutations((s) => {
				s.set('result', results);
			});
		}
	}
	return state;
};

/**
 * Merge payload into the state.
 *
 * @param acceptedActions
 * @param entityKey
 */
export const viewReducerMerger =
	(acceptedActions: Array<string>, entityKey: string) =>
	(state: Map<string, any> = Map(), dispatchedAction: Action) => {
		const action = parseActionTypeAndState(dispatchedAction);

		return actionPredicate(action.actionType, acceptedActions)
			? mergePayloadIdsIntoState(state, action, entityKey, 'result')
			: state;
	};

/**
 * Remove single entity from the state.
 *
 * @param acceptedActions
 */
export const viewReducerRemover =
	(acceptedActions: Array<string>) =>
	(state: Map<string, any> = Map(), dispatchedAction: Action) => {
		const action = parseActionTypeAndState(dispatchedAction);

		return actionPredicate(action.actionType, acceptedActions)
			? removeFromState(state, action)
			: state;
	};

/**
 * Replace payload into the state.
 *
 * @param acceptedActions
 * @param entityKey
 * @param defaultState
 */
export const viewReducerReplacer =
	(acceptedActions: Array<string>, entityKey: string, defaultState: Map<string, any> = Map()) =>
	(state: Map<string, any> = defaultState, dispatchedAction: Action) => {
		const action = parseActionTypeAndState(dispatchedAction);

		return actionPredicate(action.actionType, acceptedActions)
			? replacePayloadIdsIntoState(state, action, entityKey, 'result')
			: state;
	};

/**
 * Add contentId and action status into state, based on middleware HTTP requests.
 *
 * @param acceptedActions
 * @param defaultState
 */
export const requestStateReducer =
	(acceptedActions: Array<string>, defaultState: Map<string, any> = Map()) =>
	(state: Map<string, Map<any, any>> = defaultState, dispatchedAction: Action) => {
		const action = parseActionTypeAndState(dispatchedAction);

		if (actionPredicate(action.actionType, acceptedActions)) {
			const { contentId } = action.params;

			return state.set(contentId, Map({ status: action.actionState }));
		}

		return state;
	};
